/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Checkbox,
    Heading,
    Stack,
    Text,
    Divider,
    FormControl,
    FormLabel,
    RadioGroup,
    VisuallyHidden,
    HStack,
    Radio,
    Image,
    SimpleGrid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
// import {useCheckout} from '@salesforce/retail-react-app/app/pages/checkout/util/checkout-context'
import {useCheckout} from '../../checkout/util/checkout-context'

/* -----------------Adyen Begin ------------------------ */
import {useAdyenCheckout} from '@adyen/adyen-salesforce-pwa'
import AdyenCheckout from '../components/adyenCheckout/index'
/* -----------------Adyen End ------------------------ */

import {
    getPaymentInstrumentCardType,
    getMaskCreditCardNumber,
    getCreditCardIcon
} from '@salesforce/retail-react-app/app/utils/cc-utils'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../../components/toggle-card'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {pxToRem} from '../../../utills/pxToRem'
import GiftCardPayment from './giftCartPayment/giftCartPayment'
import TermsAndConditions from './terms-and-conditions'
import {CCLockIcon} from '../../../components/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {anotherPaypalAccountLabel, paymentSectionStyles} from './styles'
import AddressDisplay from '../../../components/address-display'
import ShippingAddressSelection from './shipping-address-selection'
import {usePromoCode, PromoCode} from './promo-code'
import {CheckIcon} from '@salesforce/retail-react-app/app/components/icons'
import PaypalOverlay from './paypal/paypalOverlay'
import {addressDetailStyle, amountStyle, paymentMethodIdStyle} from '../index.style'
import {CHECKOUT_GIFT_CARD, CHECKOUT_PAYPAL} from '../../../constants'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'

let popupCloseCheck
let ADYEN_TIME_OUT

const Payment = ({totalGiftCardValue, isAllBopisItems = false}) => {
    const {formatMessage} = useIntl()
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()
    const {
        nonStorePickShipments,
        hasOnlyEgiftCards,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        hasPaypalPaymentInstuments
    } = useCheckout()
    const selectedShippingAddress = nonStorePickShipments[0]?.shippingAddress
    const selectedBillingAddress = basket?.billingAddress
    const appliedPayment = basket?.paymentInstruments && basket?.paymentInstruments[0]
    const hasAdyenCustomSession =
        basket?.paymentInstruments &&
        basket?.paymentInstruments[0]?.paymentMethodId === 'ADYEN_CUSTOM_SESSION'
    const isTotalNotZero = (basket?.orderTotal || basket?.productTotal) !== 0
    const [billingSameAsShipping, setBillingSameAsShipping] = useState(
        !(isAllBopisItems || hasOnlyEgiftCards)
    ) // By default, have billing addr to be the same as shipping
    const [alreadyExistsPaypalPaymentDetails, setAlreadyExistsPaypalPaymentDetails] = useState(null)
    const [anotherPaypalAccountChecked, setAnotherPaypalAccountChecked] = useState(false)
    const [disableReviewOrderButton, setDisableReviewOrderButton] = useState(false)

    const popupRef = useRef(null)
    const [searchParams] = useSearchParams()

    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {mutateAsync: updatePaymentInstrumentToBasket} = useShopperBasketsMutation(
        'updatePaymentInstrumentInBasket'
    )
    const {mutateAsync: updateBillingAddressForBasket} = useShopperBasketsMutation(
        'updateBillingAddressForBasket'
    )
    const {mutateAsync: updateShippingAddressForShipment} = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const [isOrderTotalZero, setIsOrderTotalZero] = useState(false)
    const {adyenPaymentMethods, setAdyenStateData, adyenStateData} = useAdyenCheckout()
    const {step, STEPS, goToStep, goToNextStep} = useCheckout()
    const [checkoutKey, setCheckoutKey] = useState(0)
    const billingAddressForm = useForm({
        mode: 'onChange',
        shouldUnregister: false,
        defaultValues: {...selectedBillingAddress}
    })
    // Using destructuring to remove properties from the object...

    const {...promoCodeProps} = usePromoCode()

    const paymentMethodForm = useForm()

    const handleUseAnotherPaypalAccount = (e) => {
        setAnotherPaypalAccountChecked(e.target.checked)
    }

    const onPaymentSubmit = async (formValue) => {
        // The form gives us the expiration date as `MM/YY` - so we need to split it into
        // month and year to submit them as individual fields.
        const [expirationMonth, expirationYear] = formValue.expiry.split('/')

        const paymentInstrument = {
            paymentMethodId: 'AdyenComponent',
            paymentCard: {
                holder: formValue.holder,
                maskedNumber: getMaskCreditCardNumber(formValue.number),
                cardType: getPaymentInstrumentCardType(formValue.cardType),
                expirationMonth: parseInt(expirationMonth),
                expirationYear: parseInt(`20${expirationYear}`)
            }
        }

        return addPaymentInstrumentToBasket({
            parameters: {basketId: basket?.basketId},
            body: paymentInstrument
        })
    }
    const onBillingSubmit = async () => {
        if (ADYEN_TIME_OUT) {
            clearTimeout(ADYEN_TIME_OUT)
        }
        const isFormValid = await billingAddressForm.trigger()

        if (!isFormValid) {
            const firstErrorField = Object.keys(billingAddressForm.formState.errors)[0]

            if (firstErrorField) {
                billingAddressForm.setFocus(firstErrorField, {
                    shouldSelect: true
                })
            }
            return Promise.reject(new Error('Billing form is invalid'))
        }
        const billingAddress =
            !hasOnlyEgiftCards && billingSameAsShipping
                ? selectedShippingAddress
                : billingAddressForm.getValues()

        if (!billingAddress) return
        // Using destructuring to remove properties from the object...
        const {addressId, creationDate, lastModified, preferred, ...address} = billingAddress

        // If Gift Card Only orders copy billing to shipping
        if (hasOnlyEgiftCards) {
            await updateShippingAddressForShipment({
                body: address,
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: 'me'
                }
            })
        }
        return await updateBillingAddressForBasket({
            body: address,
            parameters: {basketId: basket.basketId}
        })
    }

    const removePaymentInstrument = async (paypalToken = '') => {
        const response = await refetchBasket()
        const refetchedBasket = response?.data?.baskets?.find(
            (basketItem) => basketItem.basketId == basket?.basketId
        )
        try {
            await removePaymentInstrumentFromBasket({
                parameters: {
                    basketId: basket.basketId,
                    paymentInstrumentId: refetchedBasket?.paymentInstruments?.find(
                        (ins) => ins?.c_paypalToken == paypalToken
                    )?.paymentInstrumentId
                }
            })
        } catch (e) {
            showError()
        }
    }

    const handleOverlayClose = async () => {
        clearInterval(popupCloseCheck)
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.close()
        }
        popupRef.current = null

        const response = await refetchBasket()
        const refetchedBasket = response?.data?.baskets?.find(
            (basketItem) => basketItem.basketId == basket?.basketId
        )
        const paypalPaymentInstrument = refetchedBasket?.paymentInstruments?.find(
            (ins) => ins?.paymentMethodId === CHECKOUT_PAYPAL && !ins?.c_paypalPayerID
        )
        if (paypalPaymentInstrument) {
            const paypalToken = paypalPaymentInstrument?.c_paypalToken
            removePaymentInstrument(paypalToken)
            setAnotherPaypalAccountChecked(false)
        }
    }

    const handlePaypalPayment = async () => {
        setDisableReviewOrderButton(true)
        try {
            const width = 500
            const height = 600
            const left = window.screenX + (window.innerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
            popupRef.current = window.open('', '_blank', features)

            const addPaymentInsResponse = await addPaymentInstrumentToBasket({
                parameters: {basketId: basket?.basketId},
                body: {paymentMethodId: CHECKOUT_PAYPAL}
            })

            if (addPaymentInsResponse?.c_paypalRedirectUrl) {
                popupRef.current.location.href = `${addPaymentInsResponse?.c_paypalRedirectUrl}&disable-funding=paylater,credit`
                setDisableReviewOrderButton(false)

                popupCloseCheck = setInterval(async () => {
                    if (popupRef.current?.closed) {
                        clearInterval(popupCloseCheck)
                        popupRef.current = null

                        const response = await refetchBasket()
                        const refetchedBasket = response?.data?.baskets?.find(
                            (basketItem) => basketItem.basketId == basket?.basketId
                        )
                        const paypalPaymentInstrument = refetchedBasket?.paymentInstruments?.find(
                            (ins) =>
                                ins?.paymentMethodId === CHECKOUT_PAYPAL && !ins?.c_paypalPayerID
                        )
                        if (paypalPaymentInstrument) {
                            const paypalToken = paypalPaymentInstrument?.c_paypalToken
                            popupRef.current = null
                            removePaymentInstrument(paypalToken)
                            setAnotherPaypalAccountChecked(false)
                        }
                    }
                }, 500)
            }
        } catch (error) {
            setDisableReviewOrderButton(false)
            handleOverlayClose()
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }
            showError()
        }
    }

    const onReviewOrderSubmit = async () => {
        // if total order value == 0, hide cc, paypal and disale there submit method
        const isZeroAmount = (basket?.orderTotal || basket?.productTotal) - totalGiftCardValue == 0
        if (isZeroAmount || selectedPaymentMethod !== 'CC') {
            const updatedBasket = await onBillingSubmit()
            if (updatedBasket) {
                // For zero-amount orders, add AdyenComponent payment instrument if one doesn't exist
                if (isZeroAmount) {
                    if (!basket.paymentInstruments) {
                        try {
                            const result = await addPaymentInstrumentToBasket({
                                parameters: {basketId: basket.basketId},
                                body: {
                                    paymentMethodId: 'ADYEN_CUSTOM_SESSION',
                                    paymentCard: {cardType: 'Visa'},
                                    amount: 0
                                }
                            })
                            // Refetch basket to update the context with the new payment instrument
                            const refetchResult = await refetchBasket()
                        } catch (error) {
                            let errorDetail
                            if (error?.response instanceof Response) {
                                try {
                                    errorDetail = await error.response.clone().json()
                                } catch {
                                    const text = await error.response.text()
                                    errorDetail = text
                                }
                            } else {
                                errorDetail = error?.response?.data
                            }
                            console.error('Error details:', errorDetail)
                            showError()
                            return
                        }
                    }
                }
                setTimeout(() => {
                    goToNextStep()
                }, 100)
            }
        } else {
            onSubmit()
        }
    }

    const onPaypalPaymentSuccess = async (token) => {
        const response = await refetchBasket()
        const refetchedBasket = response?.data?.baskets?.find(
            (basketItem) => basketItem.basketId == basket?.basketId
        )
        try {
            const res = await updatePaymentInstrumentToBasket({
                parameters: {
                    basketId: basket?.basketId,
                    paymentInstrumentId: refetchedBasket?.paymentInstruments?.find(
                        (instrument) => instrument?.c_paypalToken == token
                    )?.paymentInstrumentId
                },
                body: {paymentMethodId: 'PayPal'}
            })
            // remove the PI, if user has selected the feature to use another paypal account and that payment is success
            const paymentDetails = refetchedBasket?.paymentInstruments?.find(
                (ins) => ins?.paymentMethodId === 'PayPal' && ins?.c_paypalPayerID
            )
            if (paymentDetails) {
                removePaymentInstrument(paymentDetails?.c_paypalToken)
            }
            onReviewOrderSubmit()
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                    console.log('error body', errorBody)
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }
            showError()
        }
    }

    useEffect(() => {
        if (adyenStateData?.showError) {
            const ADYEN_TIME_OUT = setTimeout(() => {
                setAdyenStateData((prev) => ({
                    ...prev,
                    error: false,
                    errorMessage: '',
                    showError: false
                }))
            }, 10000) // clears error after 10 seconds
            return () => clearTimeout(ADYEN_TIME_OUT)
        }
    }, [adyenStateData?.showError])

    const onSubmit = paymentMethodForm.handleSubmit(async (paymentFormValues) => {
        if (!appliedPayment) {
            await onPaymentSubmit(paymentFormValues)
        }

        // If successful `onBillingSubmit` returns the updated basket. If the form was invalid on
        // submit, `undefined` is returned.
        const updatedBasket = await onBillingSubmit()

        if (updatedBasket) {
            goToNextStep()
        }
    })
    const onPaymentError = async (state) => {
        const lastesRes = await refetchBasket()
        const refetchedBasket = lastesRes?.data?.baskets?.find(
            (basketItem) => basketItem.basketId == basket?.basketId
        )
        setAdyenStateData((prev) => ({
            ...prev,
            error: true,
            errorMessage: refetchedBasket?.c_errorMessage || 'Payment failed. Please try again.',
            showErrorMessage:
                refetchedBasket?.c_errorMessage || 'There was a problem processing your payment.',
            showError: true
        }))
        setCheckoutKey((prev) => prev + 1)
    }

    const defaultEmptyForm = () => {
        const currentValues = billingAddressForm?.watch()
        const clearedValues = Object.keys(currentValues).reduce((acc, key) => {
            acc[key] = ''
            return acc
        }, {})
        delete clearedValues?.id
        delete clearedValues?.fullName
        clearedValues.countryCode = 'US'
        return clearedValues
    }
    const handleSelectShippingAddress = (e) => {
        billingAddressForm?.reset(defaultEmptyForm())
        setBillingSameAsShipping(e.target.checked)
    }

    const continuePayPalCheckout = () => {
        if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.focus()
        } else {
            // Optionally, reopen the popup
            // openPayPalPopup()
        }
    }

    useEffect(() => {
        setIsOrderTotalZero((basket?.orderTotal || basket?.productTotal) - totalGiftCardValue === 0)
        if ((basket?.orderTotal || basket?.productTotal) - totalGiftCardValue === 0) {
            setSelectedPaymentMethod('')
        }
    }, [basket?.orderTotal, basket?.productTotal, totalGiftCardValue])

    useEffect(() => {
        // Handle popup case
        const handleMessage = async (event) => {
            if (event.data?.source === 'cav-paypal-message') {
                const {status, token, PayerID} = event.data
                if (status === 'success') {
                    clearInterval(popupCloseCheck)
                    popupRef.current = null
                    onPaypalPaymentSuccess(token, PayerID)
                    setAnotherPaypalAccountChecked(false)
                } else if (status === 'cancel') {
                    clearInterval(popupCloseCheck)
                    const response = await refetchBasket()
                    const refetchedBasket = response?.data?.baskets?.find(
                        (basketItem) => basketItem.basketId == basket?.basketId
                    )
                    const paypalPaymentInstrument = refetchedBasket?.paymentInstruments?.find(
                        (ins) => ins?.paymentMethodId === 'PayPal' && !ins?.c_paypalPayerID
                    )
                    if (paypalPaymentInstrument) {
                        const paypalToken = paypalPaymentInstrument?.c_paypalToken
                        popupRef.current = null
                        removePaymentInstrument(paypalToken)
                        setAnotherPaypalAccountChecked(false)
                    }
                }
            }
        }
        window.addEventListener('message', handleMessage)
        return () => {
            window.removeEventListener('message', handleMessage)
            clearInterval(popupCloseCheck)
        }
    }, [])

    useEffect(() => {
        const paymentDetails = basket?.paymentInstruments?.find(
            (ins) => ins?.paymentMethodId === CHECKOUT_PAYPAL && ins?.c_paypalPayerID
        )
        if (paymentDetails) {
            setAlreadyExistsPaypalPaymentDetails(paymentDetails)
            setSelectedPaymentMethod('PP')

            setAnotherPaypalAccountChecked(false)
        }
    }, [basket?.paymentInstruments])

    useEffect(() => {
        const paymentDetails = basket?.paymentInstruments?.find(
            (ins) => ins?.paymentMethodId === CHECKOUT_PAYPAL && ins?.c_paypalPayerID
        )
        const checkPaypalSuccess = async () => {
            const updatedBasket = await onBillingSubmit()
            if (updatedBasket) {
                goToNextStep()
            }
        }
        if (paymentDetails) {
            checkPaypalSuccess()
        }
    }, [basket?.paymentInstruments])

    return (
        <ToggleCard
            id="step-3"
            step={hasOnlyEgiftCards ? 2 : isAllBopisItems ? 3 : 4}
            title={formatMessage({defaultMessage: 'Payment', id: 'checkout_payment.title.payment'})}
            editing={step === STEPS.PAYMENT}
            isLoading={
                !adyenPaymentMethods ||
                paymentMethodForm.formState.isSubmitting ||
                billingAddressForm.formState.isSubmitting
            }
            disabled={appliedPayment == null && !basket.billingAddress}
            onEdit={() => goToStep(STEPS.PAYMENT)}
            editLabel={formatMessage({
                defaultMessage: 'Edit',
                id: 'toggle_card.action.edit'
            })}
            px={[pxToRem(15), pxToRem(15), pxToRem(18), pxToRem(18)]}
        >
            <ToggleCardEdit>
                {/* billing address start here*/}
                <Stack spacing={2} sx={paymentSectionStyles} mt={0}>
                    <Heading
                        as="h3"
                        fontSize={{base: pxToRem(14), lg: pxToRem(16)}}
                        fontWeight={500}
                        lineHeight="normal"
                        letterSpacing={pxToRem(1)}
                        textTransform="none"
                        color="black"
                    >
                        <FormattedMessage
                            defaultMessage="Billing Address"
                            id="checkout_payment.heading.billing_address"
                        />
                    </Heading>
                    {!(isAllBopisItems || hasOnlyEgiftCards) && (
                        <Checkbox
                            name="billingSameAsShipping"
                            isChecked={billingSameAsShipping}
                            onChange={handleSelectShippingAddress}
                        >
                            <Text marginBottom={0} textTransform="none" position={'relative'}>
                                <FormattedMessage
                                    defaultMessage="Same as shipping address"
                                    id="checkout_payment.label.same_as_shipping"
                                />
                                <CheckIcon
                                    color="white"
                                    position="absolute"
                                    left="-20px"
                                    top="6px"
                                    width={pxToRem(15)}
                                    height={pxToRem(15)}
                                />
                            </Text>
                        </Checkbox>
                    )}
                    {!(isAllBopisItems || hasOnlyEgiftCards) &&
                        billingSameAsShipping &&
                        selectedShippingAddress && (
                            <Box marginBottom={pxToRem(25)}>
                                <AddressDisplay
                                    address={selectedShippingAddress}
                                    showAddressName={false}
                                />
                            </Box>
                        )}
                </Stack>
                {(isAllBopisItems || hasOnlyEgiftCards || !billingSameAsShipping) && (
                    <ShippingAddressSelection
                        form={billingAddressForm}
                        selectedAddress={
                            billingSameAsShipping ? selectedBillingAddress : defaultEmptyForm()
                        }
                        // formTitleAriaLabel={billingAddressAriaLabel}
                        hideSubmitButton
                        isBillingAddress
                    />
                )}
                <Divider
                    borderColor={'cav_gray'}
                    mt={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                    mb={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                />
                {/* billing address end here*/}
                <Box>
                    <PromoCode {...promoCodeProps} itemProps={{border: 'none'}} />
                </Box>
                <Divider
                    borderColor={'cav_gray'}
                    mt={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                    mb={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                />
                {/* gift card */}
                {basket?.giftCertificateItems?.length > 0 ||
                basket?.productItems?.some((product) => product?.gift == true) ? null : (
                    <>
                        <GiftCardPayment isOrderTotalZero={isOrderTotalZero} />
                    </>
                )}
                <Divider
                    borderColor={'cav_gray'}
                    mt={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                    mb={[pxToRem(20), pxToRem(20), pxToRem(30), pxToRem(30)]}
                />
                <Stack spacing={6}>
                    {!isOrderTotalZero && (
                        <Box>
                            <FormControl>
                                <VisuallyHidden>
                                    <FormLabel>Choose Payment Method</FormLabel>
                                </VisuallyHidden>
                                <RadioGroup
                                    onChange={setSelectedPaymentMethod}
                                    value={selectedPaymentMethod}
                                >
                                    <Stack direction="column" spacing={4}>
                                        <Box>
                                            <Radio value="CC" variant="payment">
                                                <HStack>
                                                    <Text as="span">
                                                        <FormattedMessage
                                                            defaultMessage="Credit Card / Wallets"
                                                            id="checkout_payment.heading.credit_card"
                                                        />
                                                    </Text>
                                                    <CCLockIcon
                                                        width="15px"
                                                        height="15px"
                                                        viewBox="0 0 15 15"
                                                    />
                                                    <Text as="span" style={{marginLeft: 'auto'}}>
                                                        <FormattedNumber
                                                            style="currency"
                                                            currency={basket?.currency}
                                                            value={
                                                                (basket?.orderTotal ||
                                                                    basket?.productTotal) -
                                                                totalGiftCardValue
                                                            }
                                                        />
                                                    </Text>
                                                </HStack>
                                            </Radio>
                                            <Box marginTop="15px">
                                                {adyenStateData?.error ||
                                                    (adyenStateData?.showError &&
                                                        selectedPaymentMethod === 'CC' && (
                                                            <p
                                                                style={{
                                                                    color: 'red',
                                                                    marginTop: '8px',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                {adyenStateData.errorMessage ||
                                                                    adyenStateData.showErrorMessage ||
                                                                    'There was a problem processing your payment.'}
                                                            </p>
                                                        ))}
                                                {adyenPaymentMethods &&
                                                    selectedPaymentMethod === 'CC' && (
                                                        <>
                                                            <AdyenCheckout
                                                                key={checkoutKey}
                                                                beforeSubmit={[onBillingSubmit]}
                                                                onError={onPaymentError}
                                                                isCustomerRegistered={false}
                                                            />
                                                            <Box
                                                                display={{
                                                                    base: 'none',
                                                                    md: 'block'
                                                                }}
                                                                px={4}
                                                                pb={pxToRem(1)}
                                                                pl={pxToRem(18)}
                                                                pr={pxToRem(18)}
                                                                bottom={0}
                                                                background="white"
                                                                borderColor="gray.100"
                                                            >
                                                                <TermsAndConditions />
                                                            </Box>
                                                        </>
                                                    )}
                                            </Box>
                                        </Box>
                                        <Radio value="PP" variant="payment">
                                            <Image
                                                src={getAssetUrl('static/img/checkout/pay-pal.svg')}
                                                alt="Error symbol"
                                                h="20px"
                                            />
                                        </Radio>
                                        {alreadyExistsPaypalPaymentDetails && (
                                            <Box>
                                                <Text sx={anotherPaypalAccountLabel}>
                                                    {
                                                        alreadyExistsPaypalPaymentDetails?.c_paypalEmail
                                                    }
                                                </Text>

                                                <Checkbox
                                                    name="useAnotherPaypalAccount"
                                                    isChecked={anotherPaypalAccountChecked}
                                                    onChange={handleUseAnotherPaypalAccount}
                                                >
                                                    <Text
                                                        marginBottom={0}
                                                        textTransform="none"
                                                        position={'relative'}
                                                    >
                                                        <FormattedMessage
                                                            defaultMessage="Use another PayPal account"
                                                            id="checkout_payment.paypal_use_another_paypal_account"
                                                        />
                                                        <CheckIcon
                                                            color="white"
                                                            position="absolute"
                                                            left="-20px"
                                                            top="6px"
                                                            width={pxToRem(15)}
                                                            height={pxToRem(15)}
                                                        />
                                                    </Text>
                                                </Checkbox>
                                            </Box>
                                        )}
                                    </Stack>
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}
                </Stack>
                {(isOrderTotalZero || selectedPaymentMethod !== 'CC') && (
                    <Box marginTop={pxToRem(25)}>
                        <Button
                            variant="red"
                            size="full"
                            isDisabled={
                                (selectedPaymentMethod === 'PP' &&
                                    (!alreadyExistsPaypalPaymentDetails ||
                                        anotherPaypalAccountChecked) &&
                                    disableReviewOrderButton) ||
                                (((isTotalNotZero && !appliedPayment) ||
                                    (!isOrderTotalZero && appliedPayment)) &&
                                    selectedPaymentMethod !== 'CC' &&
                                    selectedPaymentMethod !== 'PP')
                            }
                            onClick={
                                selectedPaymentMethod === 'PP' &&
                                (!hasPaypalPaymentInstuments ||
                                    !alreadyExistsPaypalPaymentDetails ||
                                    anotherPaypalAccountChecked)
                                    ? handlePaypalPayment
                                    : onReviewOrderSubmit
                            }
                        >
                            <FormattedMessage
                                defaultMessage="Review Order"
                                id="checkout_payment.button.review_order"
                            />
                        </Button>
                    </Box>
                )}
            </ToggleCardEdit>

            <ToggleCardSummary>
                <SimpleGrid columns={[1, 1, 2]}>
                    {appliedPayment && !hasAdyenCustomSession && (
                        <Stack>
                            <Heading as="h3" fontSize="md">
                                <FormattedMessage
                                    defaultMessage="Payment Method"
                                    id="checkout_confirmation.heading.payment_method"
                                />
                            </Heading>

                            <Stack direction="column" marginTop="0px !important">
                                {basket.paymentInstruments.map((instrument, index) => (
                                    <Box key={index} marginRight="1rem">
                                        <Text sx={paymentMethodIdStyle}>
                                            {instrument.paymentMethodId === CHECKOUT_GIFT_CARD
                                                ? 'GIFT CARD:'
                                                : instrument.paymentMethodId === 'AdyenComponent'
                                                ? 'CREDIT CARD'
                                                : instrument.paymentMethodId}
                                        </Text>

                                        <Stack direction="column">
                                            {instrument.paymentMethodId === CHECKOUT_PAYPAL && (
                                                <Text marginTop="0px !important" display="flex">
                                                    <Text sx={amountStyle}>
                                                        <FormattedMessage
                                                            defaultMessage="Amount:"
                                                            id="checkout_confirmation.amount"
                                                        />
                                                        &nbsp;
                                                    </Text>
                                                    <FormattedNumber
                                                        value={instrument.amount}
                                                        style="currency"
                                                        currency="USD"
                                                    />
                                                </Text>
                                            )}
                                            {instrument.paymentMethodId === CHECKOUT_GIFT_CARD && (
                                                <Text marginBottom="0px">
                                                    {instrument.c_cayanGiftCardMasked}
                                                </Text>
                                            )}

                                            {instrument.paymentMethodId === 'AdyenComponent' && (
                                                <Text marginBottom="0px">
                                                    {`${instrument.paymentCard?.cardType} Ending in 
                                                                    ${instrument.c_cardNo}`}
                                                </Text>
                                            )}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Stack>
                    )}

                    <Stack>
                        <Heading as="h3" fontSize="md">
                            <FormattedMessage
                                defaultMessage="Billing Address"
                                id="checkout_payment.heading.billing_address"
                            />
                        </Heading>
                        <Text sx={addressDetailStyle}>
                            <AddressDisplay
                                address={basket.billingAddress}
                                showAddressName={false}
                            />
                        </Text>
                    </Stack>
                </SimpleGrid>
            </ToggleCardSummary>
            {popupRef.current && (
                <PaypalOverlay
                    continuePayPalCheckout={continuePayPalCheckout}
                    handleOverlayClose={handleOverlayClose}
                />
            )}
        </ToggleCard>
    )
}

const PaymentCardSummary = ({payment}) => {
    const CardIcon = getCreditCardIcon(payment?.paymentCard?.cardType)
    return (
        <Stack direction="row" alignItems="center" spacing={3}>
            {CardIcon && <CardIcon layerStyle="ccIcon" />}

            <Stack direction="row">
                <Text>{payment.paymentCard.cardType}</Text>
                <Text>&bull;&bull;&bull;&bull; {payment.paymentCard.numberLastDigits}</Text>
                <Text>
                    {payment.paymentCard.expirationMonth}/{payment.paymentCard.expirationYear}
                </Text>
            </Stack>
        </Stack>
    )
}

PaymentCardSummary.propTypes = {payment: PropTypes.object}
Payment.propTypes = {totalGiftCardValue: PropTypes.number, isAllBopisItems: PropTypes.bool}

export default Payment
